import {
  BaseMessageSignerWalletAdapter,
  isIosAndRedirectable,
  isVersionedTransaction,
  scopePollingDetectionStrategy,
  WalletAccountError,
  WalletConnectionError,
  WalletDisconnectedError,
  WalletDisconnectionError,
  WalletError,
  WalletNotConnectedError,
  WalletNotReadyError,
  WalletPublicKeyError,
  WalletReadyState,
  WalletSendTransactionError,
  WalletSignMessageError,
  WalletSignTransactionError,
  type WalletName,
} from '@solana/wallet-adapter-base';
import { PublicKey, type Transaction, type TransactionSignature, type VersionedTransaction } from '@solana/web3.js';
import { getPhantomProvider } from '../utils/getPhantomProvider';

export const PhantomWalletName = 'Phantom' as WalletName<'Phantom'>;

/**
 * Phantom adapter that never falls back to `window.solana`.
 * Backpack and other wallets often register there, which caused Phantom connect
 * to open the wrong extension when multiple wallets are installed.
 */
export class CwyPhantomWalletAdapter extends BaseMessageSignerWalletAdapter {
  name = PhantomWalletName;
  url = 'https://phantom.app';
  icon =
    'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDgiIGhlaWdodD0iMTA4IiB2aWV3Qm94PSIwIDAgMTA4IDEwOCIgZmlsbD0ibm9uZSI+CjxyZWN0IHdpZHRoPSIxMDgiIGhlaWdodD0iMTA4IiByeD0iMjYiIGZpbGw9IiNBQjlGRjIiLz4KPHBhdGggZmlsbC1ydWxlPSJldmVub2RkIiBjbGlwLXJ1bGU9ImV2ZW5vZGQiIGQ9Ik00Ni41MjY3IDY5LjkyMjlDNDIuMDA1NCA3Ni44NTA5IDM0LjQyOTIgODUuNjE4MiAyNC4zNDggODUuNjE4MkMxOS41ODI0IDg1LjYxODIgMTUgODMuNjU2MyAxNSA3NS4xMzQyQzE1IDUzLjQzMDUgNDQuNjMyNiAxOS44MzI3IDcyLjEyNjggMTkuODMyN0M4Ny43NjggMTkuODMyNyA5NCAzMC42ODQ2IDk0IDQzLjAwNzlDOTQgNTguODI1OCA4My43MzU1IDc2LjkxMjIgNzMuNTMyMSA3Ni45MTIyQzcwLjI5MzkgNzYuOTEyMiA2OC43MDUzIDc1LjEzNDIgNjguNzA1MyA3Mi4zMTRDNjguNzA1MyA3MS41NzgzIDY4LjgyNzUgNzAuNzgxMiA2OS4wNzE5IDY5LjkyMjlDNjUuNTg5MyA3NS44Njk5IDU4Ljg2ODUgODEuMzg3OCA1Mi41NzU0IDgxLjM4NzhDNDcuOTkzIDgxLjM4NzggNDUuNjcxMyA3OC41MDYzIDQ1LjY3MTMgNzQuNDU5OEM0NS42NzEzIDcyLjk4ODQgNDUuOTc2OCA3MS40NTU2IDQ2LjUyNjcgNjkuOTIyOVpNODMuNjc2MSA0Mi41Nzk0QzgzLjY3NjEgNDYuMTcwNCA4MS41NTc1IDQ3Ljk2NTggNzkuMTg3NSA0Ny45NjU4Qzc2Ljc4MTYgNDcuOTY1OCA3NC42OTg5IDQ2LjE3MDQgNzQuNjk4OSA0Mi41Nzk0Qzc0LjY5ODkgMzguOTg4NSA3Ni43ODE2IDM3LjE5MzEgNzkuMTg3NSAzNy4xOTMxQzgxLjU1NzUgMzcuMTkzMSA4My42NzYxIDM4Ljk4ODUgODMuNjc2MSA0Mi41Nzk0Wk03MC4yMTAzIDQyLjU3OTVDNzAuMjEwMyA0Ni4xNzA0IDY4LjA5MTYgNDcuOTY1OCA2NS43MjE2IDQ3Ljk2NThDNjMuMzE1NyA0Ny45NjU4IDYxLjIzMyA0Ni4xNzA0IDYxLjIzMyA0Mi41Nzk1QzYxLjIzMyAzOC45ODg1IDYzLjMxNTcgMzcuMTkzMSA2NS43MjE2IDM3LjE5MzFDNjguMDkxNiAzNy4xOTMxIDcwLjIxMDMgMzguOTg4NSA3MC4yMTAzIDQyLjU3OTVaIiBmaWxsPSIjRkZGREY4Ii8+Cjwvc3ZnPg==';
  supportedTransactionVersions = new Set(['legacy', 0] as const);

  private _readyState =
    typeof window === 'undefined' || typeof document === 'undefined'
      ? WalletReadyState.Unsupported
      : WalletReadyState.NotDetected;
  private _connecting = false;
  private _wallet: NonNullable<ReturnType<typeof getPhantomProvider>> | null = null;
  private _publicKey: PublicKey | null = null;

  private _disconnected = () => {
    const wallet = this._wallet;
    if (!wallet) return;

    wallet.off?.('disconnect', this._disconnected);
    wallet.off?.('accountChanged', this._accountChanged);
    this._wallet = null;
    this._publicKey = null;
    this.emit('error', new WalletDisconnectedError());
    this.emit('disconnect');
  };

  private _accountChanged = (...args: unknown[]) => {
    if (!this._publicKey) return;

    try {
      const newPublicKey = args[0] as PublicKey;
      const next = new PublicKey(newPublicKey.toBytes());
      if (this._publicKey.equals(next)) return;
      this._publicKey = next;
      this.emit('connect', next);
    } catch (error) {
      this.emit('error', new WalletPublicKeyError((error as Error)?.message, error));
    }
  };

  constructor() {
    super();

    if (this._readyState === WalletReadyState.Unsupported) return;

    if (isIosAndRedirectable()) {
      this._readyState = WalletReadyState.Loadable;
      this.emit('readyStateChange', this._readyState);
      return;
    }

    scopePollingDetectionStrategy(() => {
      const installed = getPhantomProvider() != null;
      this._readyState = installed ? WalletReadyState.Installed : WalletReadyState.NotDetected;
      this.emit('readyStateChange', this._readyState);
      return installed;
    });
  }

  get publicKey() {
    return this._publicKey;
  }

  get connecting() {
    return this._connecting;
  }

  get connected() {
    return !!this._wallet?.isConnected && !!this._publicKey;
  }

  get readyState() {
    return this._readyState;
  }

  async connect(): Promise<void> {
    if (this.connected || this._connecting) return;

    if (this._readyState === WalletReadyState.Loadable) {
      const url = encodeURIComponent(window.location.href);
      const ref = encodeURIComponent(window.location.origin);
      window.location.href = `https://phantom.app/ul/browse/${url}?ref=${ref}`;
      return;
    }

    const wallet = getPhantomProvider();
    if (!wallet) {
      throw new WalletNotReadyError();
    }

    try {
      this._connecting = true;

      if (!wallet.isConnected) {
        try {
          await wallet.connect();
        } catch (error) {
          throw new WalletConnectionError((error as Error)?.message, error);
        }
      }

      if (!wallet.publicKey) {
        throw new WalletAccountError();
      }

      let publicKey: PublicKey;
      try {
        publicKey = new PublicKey(wallet.publicKey.toBytes());
      } catch (error) {
        throw new WalletPublicKeyError((error as Error)?.message, error);
      }

      wallet.on?.('disconnect', this._disconnected);
      wallet.on?.('accountChanged', this._accountChanged);
      this._wallet = wallet;
      this._publicKey = publicKey;
      this.emit('connect', publicKey);
    } catch (error) {
      const walletError =
        error instanceof WalletError
          ? error
          : new WalletConnectionError((error as Error)?.message, error);
      this.emit('error', walletError);
      throw walletError;
    } finally {
      this._connecting = false;
    }
  }

  async disconnect(): Promise<void> {
    const wallet = this._wallet;
    if (wallet) {
      wallet.off?.('disconnect', this._disconnected);
      wallet.off?.('accountChanged', this._accountChanged);
      this._wallet = null;
      this._publicKey = null;
      try {
        await wallet.disconnect();
      } catch (error) {
        this.emit('error', new WalletDisconnectionError((error as Error)?.message, error));
      }
    }
    this.emit('disconnect');
  }

  async sendTransaction(
    transaction: Transaction | VersionedTransaction,
    connection: Parameters<BaseMessageSignerWalletAdapter['sendTransaction']>[1],
    options: Parameters<BaseMessageSignerWalletAdapter['sendTransaction']>[2] = {}
  ): Promise<TransactionSignature> {
    const wallet = this._wallet;
    if (!wallet) throw new WalletNotConnectedError();

    try {
      const provider = wallet as NonNullable<ReturnType<typeof getPhantomProvider>> & {
        signAndSendTransaction?: (
          tx: Transaction | VersionedTransaction,
          opts?: Record<string, unknown>
        ) => Promise<{ signature: TransactionSignature }>;
      };

      if (!provider.signAndSendTransaction) {
        throw new WalletSendTransactionError('Phantom signAndSendTransaction is unavailable');
      }

      const { signers, ...sendOptions } = options as {
        signers?: Parameters<Transaction['partialSign']>;
      } & Record<string, unknown>;

      if (!isVersionedTransaction(transaction)) {
        transaction = (await this.prepareTransaction(transaction, connection, sendOptions)) as Transaction;
        if (signers?.length) transaction.partialSign(...signers);
      } else {
        if (signers?.length) transaction.sign(signers as never);
      }

      sendOptions.preflightCommitment =
        (sendOptions.preflightCommitment as string | undefined) || connection.commitment;
      const { signature } = await provider.signAndSendTransaction(transaction, sendOptions);
      return signature;
    } catch (error) {
      if (error instanceof WalletError) throw error;
      throw new WalletSendTransactionError((error as Error)?.message, error);
    }
  }

  async signTransaction<T extends Transaction | VersionedTransaction>(transaction: T): Promise<T> {
    const wallet = this._wallet;
    if (!wallet) throw new WalletNotConnectedError();

    try {
      const provider = wallet as NonNullable<ReturnType<typeof getPhantomProvider>> & {
        signTransaction?: (tx: T) => Promise<T>;
      };
      if (!provider.signTransaction) {
        throw new WalletSignTransactionError('Phantom signTransaction is unavailable');
      }
      return (await provider.signTransaction(transaction)) || transaction;
    } catch (error) {
      throw new WalletSignTransactionError((error as Error)?.message, error);
    }
  }

  async signAllTransactions<T extends Transaction | VersionedTransaction>(transactions: T[]): Promise<T[]> {
    const wallet = this._wallet;
    if (!wallet) throw new WalletNotConnectedError();

    try {
      const provider = wallet as NonNullable<ReturnType<typeof getPhantomProvider>> & {
        signAllTransactions?: (txs: T[]) => Promise<T[]>;
      };
      if (!provider.signAllTransactions) {
        throw new WalletSignTransactionError('Phantom signAllTransactions is unavailable');
      }
      return (await provider.signAllTransactions(transactions)) || transactions;
    } catch (error) {
      throw new WalletSignTransactionError((error as Error)?.message, error);
    }
  }

  async signMessage(message: Uint8Array): Promise<Uint8Array> {
    const wallet = this._wallet;
    if (!wallet) throw new WalletNotConnectedError();

    try {
      const result = await wallet.signMessage(message, 'utf8');
      if (result instanceof Uint8Array) return result;
      return result.signature;
    } catch (error) {
      throw new WalletSignMessageError((error as Error)?.message, error);
    }
  }
}
